/**
 * The Boggle Solver: every word that can be traced through a grid of letters.
 *
 * A word is found when its letters can be visited one after another, each in a cell
 * touching the last (up, down, sideways or diagonally), with no cell used twice. One cube
 * of the real game shows "Qu"; here a grid cell may hold "qu", which is two letters long
 * for spelling, scoring and minimum length, as in the official rules.
 *
 * Searching every path from every cell against 170,000 words would be slow, so the word
 * list is thinned first — a word is kept only if the grid holds enough of each of its
 * letters — and the survivors go into a prefix tree. The depth-first walk over the grid
 * then follows the tree and stops the moment no word can continue, which is what keeps a
 * 5×5 grid in the low milliseconds.
 */

export const MIN_SIZE = 3;
export const MAX_SIZE = 6;
export const DEFAULT_MIN_LENGTH = 3;

const LETTER_A = 97;
const ALPHABET = 26;
const RE_SEPARATORS = /[\s,]+/g;
const RE_LETTERS = /^[a-z]*$/;
const QU = "qu";

export type GridParse =
  | { ok: true; cells: string[] }
  | {
      ok: false;
      reason: "empty" | "invalid" | "count";
      /** How many cells the text makes, for the "count" message. */
      have: number;
    };

/**
 * Read a grid typed row by row. Spaces, commas and line breaks are ignored. With the Qu
 * cube on, a "q" (with or without its "u") is one cell holding "qu"; with it off, every
 * letter is its own cell.
 */
export function parseGrid(
  text: string,
  size: number,
  quCube: boolean
): GridParse {
  const clean = text.toLowerCase().replace(RE_SEPARATORS, "");
  if (!clean) {
    return { ok: false, reason: "empty", have: 0 };
  }
  if (!RE_LETTERS.test(clean)) {
    return { ok: false, reason: "invalid", have: 0 };
  }
  const cells: string[] = [];
  for (let i = 0; i < clean.length; i += 1) {
    if (quCube && clean[i] === "q") {
      cells.push(QU);
      if (clean[i + 1] === "u") {
        i += 1;
      }
    } else {
      cells.push(clean[i]);
    }
  }
  if (cells.length !== size * size) {
    return { ok: false, reason: "count", have: cells.length };
  }
  return { ok: true, cells };
}

/** Official scoring: 3–4 letters 1, 5 letters 2, 6 letters 3, 7 letters 5, 8+ 11. */
export function wordScore(length: number): number {
  if (length >= 8) {
    return 11;
  }
  if (length === 7) {
    return 5;
  }
  if (length === 6) {
    return 3;
  }
  if (length === 5) {
    return 2;
  }
  return length >= 3 ? 1 : 0;
}

interface TrieNode {
  kids: (TrieNode | undefined)[];
  word: string | null;
}

const node = (): TrieNode => ({
  kids: new Array<TrieNode | undefined>(ALPHABET).fill(undefined),
  word: null,
});

function buildTrie(words: readonly string[]): TrieNode {
  const root = node();
  for (const word of words) {
    let at = root;
    for (let i = 0; i < word.length; i += 1) {
      const slot = word.charCodeAt(i) - LETTER_A;
      at.kids[slot] ??= node();
      at = at.kids[slot] as TrieNode;
    }
    at.word = word;
  }
  return root;
}

/** Words the grid's letter supply could spell, ignoring adjacency. */
function candidateWords(
  list: readonly string[],
  cells: readonly string[],
  minLength: number
): string[] {
  const supply = new Int16Array(ALPHABET);
  for (const cell of cells) {
    for (const letter of cell) {
      supply[letter.charCodeAt(0) - LETTER_A] += 1;
    }
  }
  const maxLength = cells.reduce((sum, cell) => sum + cell.length, 0);
  const need = new Int16Array(ALPHABET);
  const found: string[] = [];
  for (const word of list) {
    if (word.length < minLength || word.length > maxLength) {
      continue;
    }
    need.fill(0);
    let ok = true;
    for (let i = 0; i < word.length; i += 1) {
      const slot = word.charCodeAt(i) - LETTER_A;
      need[slot] += 1;
      if (need[slot] > supply[slot]) {
        ok = false;
        break;
      }
    }
    if (ok) {
      found.push(word);
    }
  }
  return found;
}

export function neighborsOf(size: number): number[][] {
  const table: number[][] = [];
  for (let row = 0; row < size; row += 1) {
    for (let col = 0; col < size; col += 1) {
      const near: number[] = [];
      for (let dr = -1; dr <= 1; dr += 1) {
        for (let dc = -1; dc <= 1; dc += 1) {
          const r = row + dr;
          const c = col + dc;
          if (
            (dr !== 0 || dc !== 0) &&
            r >= 0 &&
            r < size &&
            c >= 0 &&
            c < size
          ) {
            near.push(r * size + c);
          }
        }
      }
      table.push(near);
    }
  }
  return table;
}

export interface BoggleWord {
  word: string;
  /** Cell indices (row-major) of one path that spells it. */
  path: number[];
  score: number;
}

export interface BoggleResult {
  words: BoggleWord[];
  score: number;
}

/** Step the tree through a cell's letters; null when no word continues. */
function enter(at: TrieNode, cell: string): TrieNode | null {
  let current: TrieNode | undefined = at;
  for (let i = 0; i < cell.length && current; i += 1) {
    current = current.kids[cell.charCodeAt(i) - LETTER_A];
  }
  return current ?? null;
}

/**
 * Every word of `minLength` letters or more that the grid spells, longest first and
 * alphabetical within a length. Each word appears once, with the first path found.
 */
export function solveBoggle(
  list: readonly string[],
  cells: readonly string[],
  size: number,
  minLength = DEFAULT_MIN_LENGTH
): BoggleResult {
  const root = buildTrie(candidateWords(list, cells, minLength));
  const near = neighborsOf(size);
  const used = new Array<boolean>(cells.length).fill(false);
  const path: number[] = [];
  const found = new Map<string, number[]>();

  function visit(index: number, from: TrieNode) {
    const at = enter(from, cells[index]);
    if (!at) {
      return;
    }
    used[index] = true;
    path.push(index);
    if (at.word !== null && !found.has(at.word)) {
      found.set(at.word, [...path]);
    }
    for (const next of near[index]) {
      if (!used[next]) {
        visit(next, at);
      }
    }
    path.pop();
    used[index] = false;
  }

  for (let start = 0; start < cells.length; start += 1) {
    visit(start, root);
  }
  const words = [...found]
    .map(([word, wordPath]) => ({
      word,
      path: wordPath,
      score: wordScore(word.length),
    }))
    .sort(
      (a, b) => b.word.length - a.word.length || a.word.localeCompare(b.word)
    );
  return { words, score: words.reduce((sum, entry) => sum + entry.score, 0) };
}
